<template>
    <section v-if="match && match.team1 && match.team2" class="match-hero">
        <div class="match-hero__meta">
            <span v-if="live" class="match-hero__live"><span class="match-hero__live-dot" />LIVE</span>
            <span class="cl-chip cl-chip--gradient">{{ getMatchTitle() }}</span>
            <span class="cl-chip">{{ phaseText }}</span>
            <span class="match-hero__date"><v-icon x-small>mdi-calendar-outline</v-icon>{{ getDate(match.matchDatetime) }}</span>
        </div>

        <div class="match-hero__board">
            <div class="match-hero__team" :style="{ '--team-color': match.team1.color || '#009688' }" @click.stop="goToTeam(match.team1)">
                <span class="match-hero__panel" />
                <span class="cl-logo match-hero__logo">
                    <img v-if="match.team1.logo" :src="`${assetsUrl}/${match.team1.logo}`" :alt="match.team1.name">
                    <v-icon v-else size="72" color="teal"> mdi-basketball-hoop </v-icon>
                </span>
                <span class="match-hero__name">{{ match.team1.name }}</span>
            </div>

            <div v-if="live || match.team1Score > 0 || match.team2Score > 0" class="match-hero__score" :class="{ 'match-hero__score--live': live }">
                <span :class="match.team1Score > match.team2Score ? 'match-hero__points--winner' : 'match-hero__points'">{{ match.team1Score }}</span>
                <span class="match-hero__dash">:</span>
                <span :class="match.team2Score > match.team1Score ? 'match-hero__points--winner' : 'match-hero__points'">{{ match.team2Score }}</span>
            </div>
            <div v-else class="match-hero__vs">VS</div>

            <div class="match-hero__team match-hero__team--away" :style="{ '--team-color': match.team2.color || '#037ade' }" @click.stop="goToTeam(match.team2)">
                <span class="match-hero__panel" />
                <span class="cl-logo match-hero__logo">
                    <img v-if="match.team2.logo" :src="`${assetsUrl}/${match.team2.logo}`" :alt="match.team2.name">
                    <v-icon v-else size="72" color="teal"> mdi-basketball-hoop </v-icon>
                </span>
                <span class="match-hero__name">{{ match.team2.name }}</span>
            </div>
        </div>
    </section>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    export default {
        props: {
            match: {
                type: Object,
                default: null
            },
            live: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                team: {},
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            phaseText() {
                const phase = this.match.cup ? 'Κύπελλο' : (this.match.phase === 'regular_season' ? 'Regular Season' : (this.match.phase === 'playoffs' ? 'Play Offs' : 'Match'))
                return (this.match.round > 0 ? `Matchday ${this.match.round} · ` : '') + phase
            }
        },
        methods: {
            getMatchTitle() {
                if (this.match.cupLeague) {
                    return this.match.cupLeague.title
                } else if (this.match.league) {
                    return this.match.league.title
                } else {
                    return 'Friendly'
                }
            },
            getDate(date) {
                return momentTimezone(date).tz('Europe/Athens').locale('el-GR').format('dddd D/M/y, HH:mm').toString()
            },
            goToTeam(team) {
                this.$router.push({ name: 'Team', params: { id: team.id } })
            }
        }
    }
</script>

<style scoped>
.match-hero {
    position: relative;
    overflow: hidden;
    padding: 24px 24px 36px;
    color: #fff;
    background:
        radial-gradient(circle at 50% 0%, rgba(3, 229, 183, 0.22) 0%, rgba(3, 229, 183, 0) 50%),
        #232323;
}

.match-hero::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.match-hero__meta {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-bottom: 24px;
}

.match-hero__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.1em;
}

.match-hero__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: match-hero-live 1.2s ease-in-out infinite;
}

@keyframes match-hero-live {
    50% {
        opacity: 0.25;
    }
}

/* live: both scores alike, framed in red, until the end of the game */
.match-hero__score--live .match-hero__points,
.match-hero__score--live .match-hero__points--winner {
    background: rgba(239, 68, 68, 0.16);
    border: 1px solid rgba(239, 68, 68, 0.6);
    box-shadow: none;
    color: #fff;
}

.match-hero__date {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
    text-transform: capitalize;
}

.match-hero__date .v-icon {
    color: #03e5b7 !important;
}

.match-hero__board {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 24px;
    max-width: 1000px;
    margin: 0 auto;
}

.match-hero__team {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    cursor: pointer;
}

/* the colour of the team behind its logo */
.match-hero__panel {
    position: absolute;
    top: -6px;
    width: 176px;
    height: 176px;
    border-radius: 16px;
    background: var(--team-color);
    transform: skewX(-8deg);
    opacity: 0.9;
}

.match-hero__logo {
    position: relative;
    width: 150px;
    height: 150px;
    margin-top: 7px;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.match-hero__name {
    position: relative;
    margin-top: 10px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    line-height: 1.05;
    text-align: center;
    text-transform: uppercase;
    overflow-wrap: anywhere;
}

.match-hero__team:hover .match-hero__name {
    color: #03e5b7;
}

.match-hero__score {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 5rem;
    line-height: 1;
}

.match-hero__points,
.match-hero__points--winner {
    min-width: 120px;
    padding: 10px 14px;
    border-radius: 10px;
    text-align: center;
}

.match-hero__points {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.65);
}

.match-hero__points--winner {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    box-shadow: 0 10px 30px rgba(3, 122, 222, 0.35);
}

.match-hero__dash {
    color: rgba(255, 255, 255, 0.35);
}

.match-hero__vs {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 4rem;
    background-image: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

@media (max-width: 700px) {
    .match-hero {
        padding: 18px 10px 28px;
    }

    .match-hero__board {
        gap: 8px;
    }

    .match-hero__panel {
        width: 96px;
        height: 96px;
    }

    .match-hero__logo {
        width: 80px;
        height: 80px;
    }

    .match-hero__name {
        font-size: 1.1rem;
    }

    .match-hero__score {
        flex-direction: column;
        gap: 4px;
        font-size: 2.6rem;
    }

    .match-hero__points,
    .match-hero__points--winner {
        min-width: 70px;
    }

    .match-hero__vs {
        font-size: 2.4rem;
    }
}
</style>
